import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { useForm, ValidationError } from "@formspree/react";

const inquiryTypes = [
  {
    value: "general",
    label: "General Inquiry",
  },
  {
    value: "product",
    label: "Product Information",
  },
  {
    value: "partnership",
    label: "Partnership Opportunity",
  },
  {
    value: "careers",
    label: "Careers",
  },
  {
    value: "support",
    label: "Technical Support",
  },
];

const ContactForm = () => {
  const [state, handleSubmit] = useForm("xdaknoae");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    organization: "",
    inquiryType: "general",
    message: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [localErrors, setLocalErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear local error when user starts typing
    if (localErrors[name]) {
      setLocalErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const missingFields = [];

    // Validate firstName (mandatory)
    if (!formData.firstName.trim()) {
      errors.firstName = "First Name is required";
      missingFields.push("First Name");
    }

    // Validate email (mandatory)
    if (!formData.email.trim()) {
      errors.email = "Email Address is required";
      missingFields.push("Email Address");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Validate organization (mandatory)
    if (!formData.organization.trim()) {
      errors.organization = "Organization is required";
      missingFields.push("Organization");
    }

    // Validate message (mandatory)
    if (!formData.message.trim()) {
      errors.message = "Message is required";
      missingFields.push("Message");
    }

    setLocalErrors(errors);
    return { isValid: Object.keys(errors).length === 0, missingFields };
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Show success message when form is submitted successfully
  useEffect(() => {
    if (state.succeeded) {
      setSnackbar({
        open: true,
        message: "Thank you for your message! We will get back to you soon.",
        severity: "success",
      });
      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        organization: "",
        inquiryType: "general",
        message: "",
      });
    }
  }, [state.succeeded]);

  // Show error message if submission fails
  useEffect(() => {
    if (
      state.errors &&
      Array.isArray(state.errors) &&
      state.errors.length > 0 &&
      state.submitCount > 0
    ) {
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  }, [state.errors, state.submitCount]);

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submitting
    const validation = validateForm();
    if (!validation.isValid) {
      const missingFieldsText = validation.missingFields.join(", ");
      setSnackbar({
        open: true,
        message: `Please fill in the following required fields: ${missingFieldsText}.`,
        severity: "error",
      });
      return;
    }

    // If validation passes, submit to Formspree
    await handleSubmit(e);
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
          Contact Us
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Fill out the form below and our team will get back to you as soon as
          possible.
        </Typography>

        <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={
                  !!localErrors.firstName ||
                  state.errors?.some((err) => err.field === "firstName") ||
                  false
                }
                helperText={
                  localErrors.firstName ||
                  state.errors?.find((err) => err.field === "firstName")
                    ?.message ||
                  ""
                }
              />
              <Box sx={{ display: "none" }}>
                <ValidationError
                  prefix="First Name"
                  field="firstName"
                  errors={state.errors}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={
                  state.errors?.some((err) => err.field === "lastName") || false
                }
                helperText={
                  state.errors?.find((err) => err.field === "lastName")
                    ?.message || ""
                }
              />
              <Box sx={{ display: "none" }}>
                <ValidationError
                  prefix="Last Name"
                  field="lastName"
                  errors={state.errors}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={
                  !!localErrors.email ||
                  state.errors?.some((err) => err.field === "email") ||
                  false
                }
                helperText={
                  localErrors.email ||
                  state.errors?.find((err) => err.field === "email")?.message ||
                  ""
                }
              />
              <Box sx={{ display: "none" }}>
                <ValidationError
                  prefix="Email"
                  field="email"
                  errors={state.errors}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                error={
                  !!localErrors.organization ||
                  state.errors?.some((err) => err.field === "organization") ||
                  false
                }
                helperText={
                  localErrors.organization ||
                  state.errors?.find((err) => err.field === "organization")
                    ?.message ||
                  ""
                }
              />
              <Box sx={{ display: "none" }}>
                <ValidationError
                  prefix="Organization"
                  field="organization"
                  errors={state.errors}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Inquiry Type"
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleChange}
              >
                {inquiryTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: "none" }}>
                <ValidationError
                  prefix="Inquiry Type"
                  field="inquiryType"
                  errors={state.errors}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                error={
                  !!localErrors.message ||
                  state.errors?.some((err) => err.field === "message") ||
                  false
                }
                helperText={
                  localErrors.message ||
                  state.errors?.find((err) => err.field === "message")
                    ?.message ||
                  ""
                }
              />
              <Box sx={{ display: "none" }}>
                <ValidationError
                  prefix="Message"
                  field="message"
                  errors={state.errors}
                />
              </Box>
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            startIcon={<Send />}
            disabled={state.submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            {state.submitting ? "Sending..." : "Send Message"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactForm;
